import { Why } from '../components/ui.jsx';
import { summarise } from '../lib/record.js';

/**
 * Routing, not diagnosis.
 *
 * Every rule here points at a platform and, where a person might reasonably be
 * worried, at a clinician. None of them names a condition. The bar is
 * deliberately conservative: this is a wellness signal from an unvalidated
 * optical measurement, and the honest thing it can offer is a next door, not an
 * opinion.
 */
function buildRoutes(record) {
  const { last, recentFailures } = summarise(record.readings);
  const recent = record.readings.slice(-8);
  const routes = [];

  if (!last) {
    routes.push({
      kind: 'info',
      platform: 'Start here',
      title: 'Take a reading first',
      body: 'There is nothing to route yet. Nothing here is ever a diagnosis — Ori notices patterns and points at the right door.',
    });
  } else {
    if (!last.locked || recentFailures >= 2) {
      routes.push({
        kind: '',
        platform: 'Calibration',
        title: 'Ori is struggling to read you',
        body: `${recentFailures} of your last ${recent.length} readings found no clear signal. That is a limitation of the model on your skin, not a finding about your health — and sharing those failures is the fastest way to fix it.`,
      });
    }
    if (last.locked && last.heartRateBpm > 100) {
      routes.push({
        kind: '',
        platform: 'ApexVitalsAI · MentorKlinikDoc',
        title: 'Resting pulse above 100',
        body: 'A resting rate this high across repeated readings is worth a clinician’s eye. ApexVitalsAI can track it properly; MentorKlinikDoc can put the question to someone qualified.',
      });
    }
    if (last.locked && last.breathsPerMinute && last.breathsPerMinute > 22) {
      routes.push({
        kind: '',
        platform: 'MentorKlinikDoc',
        title: 'Breathing faster than usual at rest',
        body: `Around ${Math.round(last.breathsPerMinute)} breaths a minute while resting. Worth mentioning at your next contact rather than acting on alone.`,
      });
    }
    if (
      last.locked &&
      last.heartRateBpm >= 50 &&
      last.heartRateBpm <= 100 &&
      (!last.breathsPerMinute || last.breathsPerMinute <= 22)
    ) {
      routes.push({
        kind: 'calm',
        platform: 'Nothing to route',
        title: 'Your readings look ordinary',
        body: 'Nothing here warrants a referral. Ori will keep watching the trend rather than any single number, which is the part that actually matters.',
      });
    }
  }

  routes.push({
    kind: 'info',
    platform: 'MedCloudMentor',
    title: 'Understand what you are looking at',
    body: 'What a pulse waveform is, why skin colour changes it, and what these numbers can and cannot tell you.',
  });
  routes.push({
    kind: 'info',
    platform: 'MentorNaijaHealthInsuranceIntel',
    title: 'Cover for what comes next',
    body: 'If a route leads to a clinic visit, what your cover does and does not include.',
  });

  return routes;
}

export default function Care({ record }) {
  const routes = buildRoutes(record);
  const enabled = record.consent.routing;

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">Where to go next</p>
        <h1>Ori notices. It does not diagnose.</h1>
        <p>
          When something in your readings is worth a second look, Ori tells you which door to knock on — and when a
          clinician should be involved, it says so plainly instead of offering an opinion.
        </p>
      </div>

      {!enabled ? (
        <div className="notice warn">
          <b>Suggestions are off</b>
          <p>
            Turn on “Suggest where to go next” under What you share to see routing. It runs entirely on this device —
            nothing is sent — but it is still your choice whether Ori offers it.
          </p>
        </div>
      ) : (
        <div className="stack">
          {routes.map((route) => (
            <div className={`route ${route.kind}`} key={route.title}>
              <span className="plat">{route.platform}</span>
              <h3>{route.title}</h3>
              <p>{route.body}</p>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop: 'var(--s2)' }}>
        <p className="eyebrow">Always true</p>
        <p style={{ fontSize: '0.95rem', color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.6 }}>
          Ori is not a medical device and none of this is medical advice. If something feels wrong, that is a reason to
          talk to a clinician regardless of what any app says — including this one.
        </p>
      </div>

      <Why>
        <strong>No competing scan app can build this layer</strong>, because building it means having already built
        nineteen clinical platforms. It turns 520,000 lines of deployed software with no acquisition channel into a
        destination for users who arrived for free.
      </Why>
    </section>
  );
}
